import { z } from 'zod';
const VISIBILITY = z.enum(['PUBLIC', 'SHARED', 'PRIVATE']);
/** Shared bound for user-supplied list/todo names. */
const NAME = z.string().trim().min(1, 'Name cannot be empty').max(200, 'Name is too long');
export const createTodoListSchema = z.object({
    name: NAME,
    visibility: VISIBILITY.optional(),
});
export const updateTodoListSchema = z
    .object({
    name: NAME.optional(),
    visibility: VISIBILITY.optional(),
})
    .refine((v) => v.name !== undefined || v.visibility !== undefined, {
    message: 'At least one of name or visibility must be provided',
});
export const setTodoListMembersSchema = z.object({
    memberIds: z
        .array(z.string().uuid({ message: 'memberIds entries must be UUIDs' }))
        .max(500, 'memberIds cannot exceed 500 entries'),
});
/**
 * Due-date wire format.
 *
 * Clients MUST send a full ISO-8601 datetime with an explicit offset or
 * `Z` (e.g. `2026-09-25T05:00:00.000Z`, i.e. `new Date(local).toISOString()`).
 * The server stores exactly what `new Date(value)` yields and never applies
 * a timezone of its own:
 *
 * - A date-only string (`2026-09-25`) is still accepted for older clients,
 *   but the ECMAScript spec parses it as **UTC midnight**, which a Sydney
 *   or Adelaide user sees as the *previous* evening and which reads as
 *   "overdue" hours early. Date-only values are NOT normalised server-side;
 *   the fix belongs in the client (convert the local picker value to UTC).
 * - A datetime without an offset (`2026-09-25T14:30`, the raw value of an
 *   `<input type="datetime-local">`) is parsed as *server-local* time, so
 *   it drifts by the server's UTC offset. Never send it raw.
 */
const DUE_DATE = z.string().refine((v) => !Number.isNaN(new Date(v).getTime()), {
    message: 'dueDate must be a valid ISO-8601 date or datetime',
});
export const createTodoSchema = z.object({
    title: NAME,
    /** See {@link DUE_DATE}: send a full ISO datetime, not a date-only string. */
    dueDate: DUE_DATE.optional().nullable(),
});
export const updateTodoSchema = z
    .object({
    title: NAME.optional(),
    /**
     * `true` marks the todo complete (completedAt = now, completedBy =
     * caller); `false` re-opens it (both cleared). Omitted = unchanged.
     */
    completed: z.boolean().optional(),
    /** See {@link DUE_DATE}: send a full ISO datetime; `null` clears it. */
    dueDate: DUE_DATE.optional().nullable(),
})
    .refine((v) => v.title !== undefined || v.completed !== undefined || v.dueDate !== undefined, {
    message: 'At least one of title, completed, or dueDate must be provided',
});
/**
 * Body for `PATCH /:listId/todos/:todoId/complete`.
 *
 * - `{ completed: true | false }` sets that exact state and is idempotent:
 *   a repeat call (or two clients racing) leaves the row unchanged and
 *   returns the current row with 200 rather than flipping it back.
 * - An empty body keeps the legacy toggle so existing clients (the Chrome
 *   extension sends `{}`) keep working unchanged.
 */
export const completeTodoSchema = z.object({
    completed: z.boolean().optional(),
});
//# sourceMappingURL=todo.schema.js.map